/**
 * The picture of one movement, by its library id — for the personal-record card's hero.
 *
 * The queue names the movement by id and carries no address: the backend resolves a movement's
 * stills only on the exercise library read, correctly in every environment (see
 * `pages/assigned-plan-calendar/model/exerciseStills`, which answers the session panel's tiles the
 * same way). That read is the entry the plan builder and the library keep warm for thirty minutes,
 * so a card usually costs no request; cold, it is one read for every record card of the sitting.
 *
 * `heroStillUrl` — the cover first, then the poster — because the box is large.
 */

import { useQuery } from '@tanstack/react-query';

import { exerciseLibraryQueryOptions, heroStillUrl, toExerciseListItems } from '~/entities/exercise-library';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

export interface ExerciseCover {
  /** The picture, or `null` where the movement has none, is not in the library, or the read failed. */
  readonly url: string | null;
  /**
   * There may yet be a picture: the movement is in the library's keeping and the library has not
   * answered. True from the FIRST render, before the card is on screen and the read is even asked —
   * the hero lays its words out for a picture on that answer, and a column that narrowed only once
   * the read began would move the words after they were drawn.
   */
  readonly pending: boolean;
}

/** @param wanted Whether the card has been on screen; `false` holds the read. */
export function useExerciseCover(exerciseId: number | null, wanted: boolean): ExerciseCover {
  const lang = useContentLanguage();
  const cover = useQuery({
    ...exerciseLibraryQueryOptions(lang),
    enabled: exerciseId !== null && wanted,
    select: (library) => {
      const movement = toExerciseListItems(library).find((item) => item.id === exerciseId);
      return movement === undefined ? null : heroStillUrl(movement);
    },
  });
  return { url: cover.data ?? null, pending: exerciseId !== null && cover.isPending };
}
